import { uuidv7Timestamp } from "@/platform/api/uuidv7"
import { readScreenScoped, removeScreenScoped, writeScreenScoped } from "@/platform/browser/session-store"
import { logger } from "@/platform/observability/logger"

export type EventType = "impression" | "click"

export interface ImpressionEvent {
  readonly serve_id: string
  readonly occurred_at: string
}

export interface ImpressionBatch {
  readonly sent_at: string
  readonly visit_id: string
  readonly impressions: ImpressionEvent[]
  readonly clicks: ImpressionEvent[]
}

export interface SendResult {
  readonly status: number
}

export type Sender = (batch: ImpressionBatch) => Promise<SendResult>

type SendOutcome = "success" | "drop" | "retry"

export const classify = (status: number): SendOutcome => {
  if (status >= 200 && status < 300) return "success"
  if (status === 429) return "retry"
  if (status >= 400 && status < 500) return "drop"
  return "retry"
}

export const nextBackoffMs = (current: number, initialMs: number, maxMs: number): number => Math.min(current === 0 ? initialMs : current * 2, maxMs)

export const isServeIdExpired = (serveId: string, now: number, maxAgeMs: number): boolean => {
  const servedAt = uuidv7Timestamp(serveId)
  return servedAt !== null && now - servedAt > maxAgeMs
}

export interface QueueEntryInput {
  readonly type: EventType
  readonly serve_id: string
  readonly occurred_at: string
  readonly visit_id: string
}

export interface QueueEntry extends QueueEntryInput {
  readonly seq: number
  readonly enqueuedAt: number
}

const isQueueEntry = (value: unknown): value is QueueEntry => {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  return (
    Number.isFinite(v.seq) &&
    (v.type === "impression" || v.type === "click") &&
    typeof v.serve_id === "string" &&
    typeof v.occurred_at === "string" &&
    typeof v.visit_id === "string" &&
    Number.isFinite(v.enqueuedAt)
  )
}

const toWireEvent = ({ serve_id, occurred_at }: QueueEntryInput): ImpressionEvent => ({ serve_id, occurred_at })

export const buildBatch = (entries: ReadonlyArray<QueueEntryInput>, sentAt: Date): ImpressionBatch => ({
  sent_at: sentAt.toISOString(),
  visit_id: entries[0]?.visit_id ?? "",
  impressions: entries.filter((e) => e.type === "impression").map(toWireEvent),
  clicks: entries.filter((e) => e.type === "click").map(toWireEvent),
})

const groupByVisit = <T extends QueueEntryInput>(entries: ReadonlyArray<T>): T[][] => {
  const groups = new Map<string, T[]>()
  for (const entry of entries) {
    const group = groups.get(entry.visit_id)
    if (group) group.push(entry)
    else groups.set(entry.visit_id, [entry])
  }
  return [...groups.values()]
}

export const chunkForBeacon = <T extends QueueEntryInput>(entries: ReadonlyArray<T>, sentAt: Date, maxBytes: number, maxCount: number): T[][] => {
  const encoder = new TextEncoder()
  const envelopeBytesFor = (visitId: string) => encoder.encode(JSON.stringify({ sent_at: sentAt.toISOString(), visit_id: visitId, impressions: [], clicks: [] })).length
  const eventBytes = (entry: T) => encoder.encode(JSON.stringify(toWireEvent(entry))).length + 1

  const chunks: T[][] = []

  for (const group of groupByVisit(entries)) {
    const envelopeBytes = envelopeBytesFor(group[0]?.visit_id ?? "")
    let current: T[] = []
    let currentBytes = envelopeBytes

    for (const entry of group) {
      const size = eventBytes(entry)
      if (current.length > 0 && (current.length >= maxCount || currentBytes + size > maxBytes)) {
        chunks.push(current)
        current = []
        currentBytes = envelopeBytes
      }
      current.push(entry)
      currentBytes += size
    }
    if (current.length > 0) chunks.push(current)
  }

  return chunks
}

export interface ImpressionsQueueOptions {
  send: Sender
  now?: () => number
  storageKey?: string
  sizeThreshold?: number
  ageThresholdMs?: number
  maxBatchEvents?: number
  maxQueueEvents?: number
  maxServeIdAgeMs?: number
  initialBackoffMs?: number
  maxBackoffMs?: number
  persistThrottleMs?: number
}

const DEFAULTS = {
  storageKey: "impressions-queue",
  sizeThreshold: 50,
  ageThresholdMs: 10_000,
  maxBatchEvents: 200,
  maxQueueEvents: 5_000,
  maxServeIdAgeMs: 24 * 60 * 60 * 1000,
  initialBackoffMs: 5_000,
  maxBackoffMs: 5 * 60 * 1000,
  persistThrottleMs: 250,
} as const

export class ImpressionsQueue {
  private pending: QueueEntry[]
  private nextSeq: number
  private readonly inFlightSeqs = new Set<number>()
  private readonly send: Sender
  private readonly now: () => number
  private readonly storageKey: string
  private readonly sizeThreshold: number
  private readonly ageThresholdMs: number
  private readonly maxBatchEvents: number
  private readonly maxQueueEvents: number
  private readonly maxServeIdAgeMs: number
  private readonly initialBackoffMs: number
  private readonly maxBackoffMs: number
  private readonly persistThrottleMs: number
  private backoffMs = 0
  private nextRetryAt: number | null = null
  private inFlightFlush: Promise<void> | null = null
  private persistTimer: ReturnType<typeof setTimeout> | null = null
  private persistDirty = false

  constructor(options: ImpressionsQueueOptions) {
    this.send = options.send
    this.now = options.now ?? Date.now
    this.storageKey = options.storageKey ?? DEFAULTS.storageKey
    this.sizeThreshold = options.sizeThreshold ?? DEFAULTS.sizeThreshold
    this.ageThresholdMs = options.ageThresholdMs ?? DEFAULTS.ageThresholdMs
    this.maxBatchEvents = options.maxBatchEvents ?? DEFAULTS.maxBatchEvents
    this.maxQueueEvents = options.maxQueueEvents ?? DEFAULTS.maxQueueEvents
    this.maxServeIdAgeMs = options.maxServeIdAgeMs ?? DEFAULTS.maxServeIdAgeMs
    this.initialBackoffMs = options.initialBackoffMs ?? DEFAULTS.initialBackoffMs
    this.maxBackoffMs = options.maxBackoffMs ?? DEFAULTS.maxBackoffMs
    this.persistThrottleMs = options.persistThrottleMs ?? DEFAULTS.persistThrottleMs
    this.pending = this.hydrate()
    this.nextSeq = this.pending.reduce((max, e) => Math.max(max, e.seq), -1) + 1
  }

  get size(): number {
    return this.pending.length
  }

  private hydrate(): QueueEntry[] {
    const raw = readScreenScoped(this.storageKey)
    if (!raw) return []

    try {
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter(isQueueEntry) : []
    } catch {
      return []
    }
  }

  private writePersist(): void {
    this.persistDirty = false
    if (this.pending.length === 0) {
      removeScreenScoped(this.storageKey)
      return
    }
    writeScreenScoped(this.storageKey, JSON.stringify(this.pending))
  }

  private persist(): void {
    this.persistDirty = true
    if (this.persistTimer !== null) return

    this.writePersist()
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null
      if (this.persistDirty) this.writePersist()
    }, this.persistThrottleMs)
  }

  private persistNow(): void {
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer)
      this.persistTimer = null
    }
    this.writePersist()
  }

  private dueForRetry(): boolean {
    return this.nextRetryAt === null || this.now() >= this.nextRetryAt
  }

  enqueue(input: QueueEntryInput): void {
    const entry: QueueEntry = { ...input, seq: this.nextSeq++, enqueuedAt: this.now() }
    this.pending.push(entry)

    if (this.pending.length > this.maxQueueEvents) {
      let overflow = this.pending.length - this.maxQueueEvents
      this.pending = this.pending.filter((e) => {
        if (overflow === 0 || this.inFlightSeqs.has(e.seq)) return true
        overflow -= 1
        return false
      })
    }

    this.persist()

    if (this.pending.length >= this.sizeThreshold && this.dueForRetry()) void this.flush()
  }

  private notInFlight(): QueueEntry[] {
    return this.pending.filter((e) => !this.inFlightSeqs.has(e.seq))
  }

  tick(): void {
    const candidates = this.notInFlight()
    if (candidates.length === 0) return

    if (this.nextRetryAt !== null) {
      if (this.dueForRetry()) void this.flush()
      return
    }

    const oldest = Math.min(...candidates.map((e) => e.enqueuedAt))
    if (this.now() - oldest >= this.ageThresholdMs) void this.flush()
  }

  takeAllForBeacon(): QueueEntry[] {
    this.dropExpired()

    const eligible = this.notInFlight()
    this.pending = this.pending.filter((e) => this.inFlightSeqs.has(e.seq))
    this.persistNow()
    return eligible
  }

  requeue(events: ReadonlyArray<QueueEntry>): void {
    if (events.length === 0) return
    this.pending = [...events, ...this.pending].slice(-this.maxQueueEvents)
    this.persistNow()
  }

  private dropExpired(): void {
    const before = this.pending.length
    this.pending = this.pending.filter((e) => this.inFlightSeqs.has(e.seq) || !isServeIdExpired(e.serve_id, this.now(), this.maxServeIdAgeMs))
    if (this.pending.length !== before) {
      logger.error(`impressions: dropped ${before - this.pending.length} event(s) older than 24h before sending`)
      this.persist()
    }
  }

  flush(): Promise<void> {
    this.inFlightFlush ??= this.runFlush().finally(() => {
      this.inFlightFlush = null
    })
    return this.inFlightFlush
  }

  private async runFlush(): Promise<void> {
    for (;;) {
      this.dropExpired()

      const candidates = this.notInFlight()
      if (candidates.length === 0) return

      const visitId = candidates[0]?.visit_id
      const batch: QueueEntry[] = []
      for (const entry of candidates) {
        if (entry.visit_id !== visitId) continue
        batch.push(entry)
        if (batch.length >= this.maxBatchEvents) break
      }

      const seqs = new Set(batch.map((e) => e.seq))
      for (const seq of seqs) this.inFlightSeqs.add(seq)

      const wire = buildBatch(batch, new Date(this.now()))

      let result: SendResult
      try {
        result = await this.send(wire)
      } catch {
        result = { status: 0 }
      } finally {
        for (const seq of seqs) this.inFlightSeqs.delete(seq)
      }

      const outcome = classify(result.status)

      if (outcome === "retry") {
        this.backoffMs = nextBackoffMs(this.backoffMs, this.initialBackoffMs, this.maxBackoffMs)
        this.nextRetryAt = this.now() + this.backoffMs
        return
      }

      if (outcome === "drop") logger.error(`impressions: dropped a batch of ${batch.length} event(s) after a ${result.status} response`)

      this.pending = this.pending.filter((e) => !seqs.has(e.seq))
      this.backoffMs = 0
      this.nextRetryAt = null
      this.persist()
    }
  }
}
